import { makeRequest } from './https';

// OneSignal App ID - same as mobile app
const ONESIGNAL_APP_ID = 'fd8e6980-a87f-4afd-a10c-f84192514c7b';

class OneSignalService {
  private isInitialized = false;
  private OneSignal: any = null;

  // Initialize OneSignal
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      console.log('OneSignal: Already initialized');
      return;
    }

    console.log('OneSignal: Initializing with App ID:', ONESIGNAL_APP_ID);

    try {
      // Wait for OneSignal to be available
      if (typeof window === 'undefined' || !(window as any).OneSignal) {
        console.warn('OneSignal: SDK not loaded yet, waiting...');
        await this.waitForOneSignal();
      }

      this.OneSignal = (window as any).OneSignal;

      // Initialize OneSignal
      console.log('OneSignal: Starting init with App ID:', ONESIGNAL_APP_ID);
      
      await this.OneSignal.init({
        appId: ONESIGNAL_APP_ID,
      });

      console.log('OneSignal: Init completed, setting up handlers...');

      // Setup notification handlers
      this.setupNotificationHandlers();

      this.isInitialized = true;
      console.log('OneSignal: Successfully initialized and ready!');
    } catch (error) {
      console.error('OneSignal: Initialization failed:', error);
      console.warn('OneSignal: Continuing without push notifications');
    }
  }

  // Wait for OneSignal SDK to load
  private waitForOneSignal(): Promise<void> {
    return new Promise((resolve) => {
      // Check if already available
      if ((window as any).OneSignal) {
        resolve();
        return;
      }

      const checkInterval = setInterval(() => {
        if ((window as any).OneSignal) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);

      // Timeout after 15 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        console.warn('OneSignal: Timeout waiting for SDK to load');
        resolve();
      }, 15000);
    });
  }

  // Check if OneSignal is ready
  isReady(): boolean {
    return this.isInitialized && this.OneSignal !== null;
  }

  // Setup notification event handlers
  private setupNotificationHandlers() {
    try {
      if (!this.OneSignal) return;

      // Handle notifications received in foreground
      this.OneSignal.Notifications.addEventListener('foregroundWillDisplay', (event: any) => {
        console.log('🔔 OneSignal: Notification received in foreground:', event);
        
        const notification = event.notification;
        console.log('🔔 Notification data:', {
          id: notification.notificationId,
          title: notification.title,
          body: notification.body,
          data: notification.additionalData
        });

        // Display the notification (v16 API)
        // The notification will be displayed automatically, no need to call display()
      });

      // Handle notification clicks
      this.OneSignal.Notifications.addEventListener('click', (event: any) => {
        console.log('🔔 OneSignal: Notification clicked:', event);
        
        const notification = event.notification;
        const additionalData = notification.additionalData;

        // Handle notification click actions
        this.handleNotificationClick(additionalData);
      });

      // Handle permission changes
      this.OneSignal.Notifications.addEventListener('permissionChange', (event: any) => {
        console.log('🔔 OneSignal: Permission changed:', event);
      });

      // Handle subscription changes
      this.OneSignal.User.PushSubscription.addEventListener('change', (event: any) => {
        console.log('🔔 OneSignal: Push subscription changed:', event);
        
        if (event.current.id) {
          console.log('🔔 New Subscription ID:', event.current.id);
        }
      });

      console.log('OneSignal: Notification handlers setup complete');
    } catch (error) {
      console.error('OneSignal: Failed to setup handlers:', error);
    }
  }

  // Get subscription ID (player ID) - wait for valid subscription
  async getPlayerId(): Promise<string | null> {
    try {
      if (!this.OneSignal) {
        console.warn('OneSignal: SDK not available');
        return null;
      }

      // Get current subscription ID
      const subscriptionId = this.OneSignal.User.PushSubscription.id;
      
      // If it's already a valid UUID, return it immediately
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (subscriptionId && uuidRegex.test(subscriptionId)) {
        console.log('OneSignal: Valid Subscription ID:', subscriptionId);
        return subscriptionId;
      }

      // If pending or invalid, wait for the change event (max 10 seconds)
      console.log('OneSignal: Subscription is pending, waiting for change event...');
      
      return new Promise((resolve) => {
        const timeout = setTimeout(() => {
          console.warn('OneSignal: Timeout waiting for valid subscription ID');
          resolve(null);
        }, 10000); // 10 second timeout

        // Listen for subscription change
        const unsubscribe = this.OneSignal.User.PushSubscription.addEventListener('change', (change: any) => {
          const newId = change.current.id;
          
          if (newId && newId !== 'pending' && uuidRegex.test(newId)) {
            console.log('OneSignal: Valid Subscription ID received:', newId);
            clearTimeout(timeout);
            unsubscribe();
            resolve(newId);
          }
        });
      });
    } catch (error) {
      console.error('OneSignal: Failed to get subscription ID:', error);
      return null;
    }
  }

  // Register player with backend API
  async registerPlayerWithBackend(organizationId: string, userId: string): Promise<boolean> {
    try {
      console.log('OneSignal: Registering player with backend...');

      const playerId = await this.getPlayerId();
      if (!playerId) {
        console.warn('OneSignal: No player ID available for registration');
        return false;
      }

      await makeRequest({
        url: '/notification/register-player',
        method: 'post',
        data: {
          organization_id: organizationId,
          player_id: playerId,
          platform: 'web',
          external_user_id: userId
        }
      });

      console.log('OneSignal: Player registered with backend successfully');
      return true;
    } catch (error) {
      console.error('OneSignal: Failed to register player with backend:', error);
      return false;
    }
  }

  // Unregister player from backend API
  async unregisterPlayerFromBackend(): Promise<boolean> {
    try {
      console.log('OneSignal: Unregistering player from backend...');

      const playerId = await this.getPlayerId();
      if (!playerId) {
        console.warn('OneSignal: No player ID available for unregistration');
        return false;
      }

      await makeRequest({
        url: `/notification/unregister-player/${playerId}`,
        method: 'post',
        data: {}
      });

      console.log('OneSignal: Player unregistered from backend successfully');
      return true;
    } catch (error) {
      console.error('OneSignal: Failed to unregister player from backend:', error);
      return false;
    }
  }

  // Set external user ID
  async setExternalUserId(userId: string) {
    try {
      if (!this.OneSignal) {
        console.warn('OneSignal: SDK not available');
        return;
      }

      await this.OneSignal.login(userId);
      console.log('OneSignal: External user ID set:', userId);
    } catch (error) {
      console.error('OneSignal: Failed to set external user ID:', error);
    }
  }

  // Set tags for segmentation
  async setTags(tags: { [key: string]: string }) {
    try {
      if (!this.OneSignal) {
        console.warn('OneSignal: SDK not available');
        return;
      }

      await this.OneSignal.User.addTags(tags);
      console.log('OneSignal: Tags set:', tags);
    } catch (error) {
      console.error('OneSignal: Failed to set tags:', error);
    }
  }

  // Get current tags
  async getTags(): Promise<{ [key: string]: string } | null> {
    try {
      if (!this.OneSignal) {
        console.warn('OneSignal: SDK not available');
        return null;
      }

      const tags = await this.OneSignal.User.getTags();
      console.log('OneSignal: Current tags:', tags);
      return tags;
    } catch (error) {
      console.error('OneSignal: Failed to get tags:', error);
      return null;
    }
  }

  // Handle notification click actions based on backend specifications
  private handleNotificationClick(additionalData: any) {
    if (!additionalData) {
      console.log('OneSignal: No additional data in notification');
      return;
    }

    console.log('OneSignal: Handling notification click:', additionalData);

    // Handle different notification types
    switch (additionalData.type) {
      case 'new_message':
        // Navigate to chat for the organization
        console.log('Navigate to chat for organization:', additionalData.organization_id);
        console.log('Message ID:', additionalData.chat_message_id);
        console.log('Message type:', additionalData.message_type || 'text');
        
        // Navigate using React Router
        if (additionalData.organization_id) {
          window.location.href = `/dashboard/chat?org=${additionalData.organization_id}`;
        }
        break;

      case 'new_invoice':
        // Navigate to invoices
        console.log('Navigate to invoices for organization:', additionalData.organization_id);
        console.log('Invoice ID:', additionalData.invoice_id);
        console.log('Invoice type:', additionalData.invoice_type);
        
        if (additionalData.organization_id) {
          window.location.href = `/dashboard/invoices?org=${additionalData.organization_id}`;
        }
        break;

      case 'new_report':
        // Navigate to reports
        console.log('Navigate to reports for organization:', additionalData.organization_id);
        console.log('Report ID:', additionalData.report_id);
        console.log('Report type:', additionalData.report_type);
        
        if (additionalData.organization_id) {
          window.location.href = `/dashboard/reports?org=${additionalData.organization_id}`;
        }
        break;

      default:
        console.log('Unknown notification type:', additionalData.type);
        console.log('Falling back to home screen');
        window.location.href = '/dashboard';
    }
  }

  // Check notification permission status
  async getNotificationPermission(): Promise<boolean> {
    try {
      if (!this.OneSignal) {
        console.warn('OneSignal: SDK not available');
        return false;
      }

      const permission = await this.OneSignal.Notifications.permission;
      console.log('OneSignal: Notification permission:', permission);
      return permission;
    } catch (error) {
      console.error('OneSignal: Failed to get notification permission:', error);
      return false;
    }
  }

  // Request notification permission
  async requestNotificationPermission(): Promise<boolean> {
    try {
      if (!this.OneSignal) {
        console.warn('OneSignal: SDK not available');
        return false;
      }

      const result = await this.OneSignal.Notifications.requestPermission();
      console.log('OneSignal: Permission request result:', result);
      return result;
    } catch (error) {
      console.error('OneSignal: Failed to request notification permission:', error);
      return false;
    }
  }

  // Opt in/out of push notifications
  async setPushSubscription(subscribe: boolean) {
    try {
      if (!this.OneSignal) {
        console.warn('OneSignal: SDK not available');
        return;
      }

      if (subscribe) {
        await this.OneSignal.User.PushSubscription.optIn();
        console.log('OneSignal: Opted in to push notifications');
      } else {
        await this.OneSignal.User.PushSubscription.optOut();
        console.log('OneSignal: Opted out of push notifications');
      }
    } catch (error) {
      console.error('OneSignal: Failed to set push subscription:', error);
    }
  }

  // Debug method to check OneSignal status
  async getDebugInfo() {
    try {
      console.log('🔔 === OneSignal Debug Info ===');

      if (!this.OneSignal) {
        console.log('🔔 OneSignal not available');
        console.log('🔔 === End Debug Info ===');
        return null;
      }

      console.log('🔔 OneSignal available:', !!this.OneSignal);

      // Check notification permission
      const hasPermission = await this.getNotificationPermission();
      console.log('🔔 Notification permission:', hasPermission);

      // Get player ID
      const playerId = await this.getPlayerId();
      console.log('🔔 Player ID:', playerId);

      // Get current tags
      const tags = await this.getTags();
      console.log('🔔 Current tags:', tags);

      // Get external user ID
      const externalUserId = await this.OneSignal.User.getExternalId();
      console.log('🔔 External user ID:', externalUserId);

      console.log('🔔 === End Debug Info ===');

      return {
        isAvailable: true,
        hasPermission,
        playerId,
        tags,
        externalUserId
      };
    } catch (error) {
      console.error('🔔 Error in getDebugInfo:', error);
      return null;
    }
  }
}

export default new OneSignalService();

